class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedOnce) return;
    this.isConnectedOnce = true;

    this.dialog = this.querySelector('[data-newsletter-dialog]');
    this.closeButton = this.querySelector('[data-newsletter-close]');
    this.emailInput = this.querySelector('[data-newsletter-email]');
    this.feedback = this.querySelector('[data-newsletter-feedback]');
    this.mediaImage = this.querySelector('[data-newsletter-image]');
    this.storageKey = this.dataset.newsletterStorageKey || 'mondo:newsletter-home-seen:v2';
    this.sessionStartedKey = `${this.storageKey}:session-started`;
    this.openDelay = Number.parseInt(this.dataset.newsletterOpenDelay || '1000', 10);
    this.mediaLeadTime = 3000;

    if (!this.dialog || !this.closeButton) return;

    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleDialogClick = this.handleDialogClick.bind(this);
    this.handleDialogCancel = this.handleDialogCancel.bind(this);
    this.handleDialogKeydown = this.handleDialogKeydown.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);

    this.closeButton.addEventListener('click', this.handleCloseClick);
    this.dialog.addEventListener('click', this.handleDialogClick);
    this.dialog.addEventListener('cancel', this.handleDialogCancel);
    this.dialog.addEventListener('keydown', this.handleDialogKeydown);
    this.dialog.addEventListener('close', this.handleDialogClose);
    document.addEventListener('click', this.handleDocumentClick);

    if (this.feedback) {
      this.prepareMedia();
      this.openTimer = window.setTimeout(() => this.open({ focusEmail: false }), 0);
      return;
    }

    if (this.dataset.newsletterAutoOpen === 'true' && !this.hasBeenSeen()) {
      const remainingDelay = this.getRemainingDelay();
      this.mediaTimer = window.setTimeout(
        () => this.prepareMedia(),
        Math.max(0, remainingDelay - this.mediaLeadTime)
      );
      this.openTimer = window.setTimeout(
        () => this.open({ focusEmail: false }),
        remainingDelay
      );
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.mediaTimer);
    this.closeButton?.removeEventListener('click', this.handleCloseClick);
    this.dialog?.removeEventListener('click', this.handleDialogClick);
    this.dialog?.removeEventListener('cancel', this.handleDialogCancel);
    this.dialog?.removeEventListener('keydown', this.handleDialogKeydown);
    this.dialog?.removeEventListener('close', this.handleDialogClose);
    document.removeEventListener('click', this.handleDocumentClick);
    this.isConnectedOnce = false;
  }

  hasBeenSeen() {
    try {
      return window.localStorage.getItem(this.storageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  rememberSeen() {
    try {
      window.localStorage.setItem(this.storageKey, 'true');
    } catch (error) {
      // The popup still works when storage is unavailable.
    }
  }

  getRemainingDelay() {
    const delay = Number.isFinite(this.openDelay) ? this.openDelay : 180000;

    try {
      const now = Date.now();
      const savedStartedAt = Number.parseInt(window.sessionStorage.getItem(this.sessionStartedKey), 10);
      const startedAt = Number.isFinite(savedStartedAt) ? savedStartedAt : now;

      if (!Number.isFinite(savedStartedAt)) {
        window.sessionStorage.setItem(this.sessionStartedKey, String(startedAt));
      }

      return Math.max(0, delay - (now - startedAt));
    } catch (error) {
      return delay;
    }
  }

  handleCloseClick() {
    this.close();
  }

  handleDialogClick(event) {
    if (event.target === this.dialog) this.close();
  }

  handleDialogCancel(event) {
    event.preventDefault();
    this.close();
  }

  handleDialogKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest?.('[data-nl-summon]');
    if (!trigger) return;
    event.preventDefault();
    this.opener = trigger;
    this.open({ focusEmail: true });
  }

  handleDialogClose() {
    this.dialog.classList.remove('is-visible');
    this.closeButton.removeAttribute('aria-busy');
  }

  prepareMedia() {
    if (!this.mediaImage || this.mediaImage.dataset.mediaReady === 'true') return;

    const { src, srcset, sizes } = this.mediaImage.dataset;
    if (sizes) this.mediaImage.sizes = sizes;
    if (srcset) this.mediaImage.srcset = srcset;
    if (src) this.mediaImage.src = src;
    this.mediaImage.dataset.mediaReady = 'true';
  }

  open({ focusEmail = false } = {}) {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.mediaTimer);
    this.prepareMedia();
    this.rememberSeen();

    if (!this.dialog.open) this.dialog.showModal();

    requestAnimationFrame(() => {
      this.dialog.classList.add('is-visible');
      if (focusEmail) this.emailInput?.focus({ preventScroll: true });
      else this.closeButton.focus({ preventScroll: true });
    });
  }

  close() {
    if (!this.dialog.open || this.closeButton.getAttribute('aria-busy') === 'true') return;

    this.closeButton.setAttribute('aria-busy', 'true');
    this.dialog.classList.remove('is-visible');

    const finishClose = () => {
      this.dialog.close();
      this.opener?.focus({ preventScroll: true });
      this.opener = null;
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClose();
      return;
    }

    this.closeTimer = window.setTimeout(finishClose, 220);
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
